import React, { useState, useEffect } from 'react';
import {
    Page,
    Layout,
    Card,
    Button,
    FormLayout,
    Select,
    TextField,
    Spinner,
    Banner,
    InlineStack,
    Text,
    BlockStack,
    DataTable,
    Badge
} from '@shopify/polaris';
import { FinancialSummaryCard } from '../components/accounting/FinancialSummaryCard';
import { RevenueChart, RevenueVsExpenseChart } from '../components/accounting/RevenueChart';
import { ExpenseSummary } from '../components/accounting/ExpenseTable';
import { reportsService } from '../services/reportsService';
import {
    accountingHelpers,
    getAccountingPeriodOptions,
    REPORT_TYPES
} from '../constants/accountingConstants';

export const ReportsPage = () => {
    const [reportType, setReportType] = useState('profit_loss');
    const [periodType, setPeriodType] = useState('current_month');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [groupBy, setGroupBy] = useState('monthly');
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const reportTypeOptions = [
        { label: 'Profit & Loss', value: 'profit_loss' },
        { label: 'Product Profitability', value: 'product_profitability' },
        { label: 'Customer Profitability', value: 'customer_profitability' },
        { label: 'Revenue Breakdown', value: 'revenue_breakdown' },
        { label: 'Expense Breakdown', value: 'expense_breakdown' },
        { label: 'Payment Method Analysis', value: 'payment_method_analysis' }
    ];

    const groupByOptions = [
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Quarterly', value: 'quarterly' }
    ];

    useEffect(() => {
        if (periodType !== 'custom') {
            loadReport();
        }
    }, [reportType, periodType, groupBy]);

    const loadReport = async () => {
        try {
            setLoading(true);
            setError(null);

            let data;
            const params = periodType === 'custom'
                ? { start_date: startDate, end_date: endDate, group_by: groupBy }
                : { period: periodType, group_by: groupBy };

            switch (reportType) {
                case 'profit_loss':
                    data = await reportsService.getProfitLossReport(params);
                    break;
                case 'product_profitability':
                    data = await reportsService.getProductProfitabilityReport(params);
                    break;
                case 'customer_profitability':
                    data = await reportsService.getCustomerProfitabilityReport(params);
                    break;
                case 'revenue_breakdown':
                    data = await reportsService.getRevenueBreakdownReport(params);
                    break;
                case 'expense_breakdown':
                    data = await reportsService.getExpenseBreakdownReport(params);
                    break;
                case 'payment_method_analysis':
                    data = await reportsService.getPaymentMethodAnalysisReport(params);
                    break;
                default:
                    data = await reportsService.getProfitLossReport(params);
            }

            setReportData(data);

        } catch (err) {
            console.error('Error loading report:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = () => {
        if (periodType === 'custom' && (!startDate || !endDate)) {
            setError('Please select both start and end dates');
            return;
        }
        loadReport();
    };

    const renderProfitLossReport = () => {
        if (!reportData) return null;

        const { summary, trend } = reportData;

        return (
            <BlockStack gap="400">
                {/* Summary Metrics */}
                <Layout>
                    <Layout.Section>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '1rem'
                        }}>
                            <FinancialSummaryCard
                                title="Total Revenue"
                                amount={summary.total_revenue}
                                status="revenue"
                            />
                            <FinancialSummaryCard
                                title="Total Expenses"
                                amount={summary.total_expenses}
                                status="expense"
                            />
                            <FinancialSummaryCard
                                title="Gross Profit"
                                amount={summary.gross_profit}
                                status="profit"
                            />
                            <FinancialSummaryCard
                                title="Net Profit"
                                amount={summary.net_profit}
                                status="profit"
                            />
                        </div>
                    </Layout.Section>

                    {trend && trend.length > 0 && (
                        <Layout.Section>
                            <RevenueVsExpenseChart
                                revenueData={trend.map(t => ({ period: t.period, revenue: t.revenue }))}
                                expenseData={trend.map(t => ({ period: t.period, total_amount: t.expenses }))}
                                title="Revenue vs Expenses Trend"
                            />
                        </Layout.Section>
                    )}

                    <Layout.Section>
                        <Card>
                            <BlockStack gap="400">
                                <Text variant="headingMd" as="h2">
                                    Profit & Loss Details
                                </Text>
                                <div style={{ borderTop: '1px solid #e1e3e5', paddingTop: '1rem' }}>
                                    <DataTable
                                        columnContentTypes={['text', 'numeric']}
                                        headings={['Item', 'Amount']}
                                        rows={[
                                            ['Total Revenue', <Text fontWeight="semibold">{accountingHelpers.formatCurrency(summary.total_revenue)}</Text>],
                                            ['Direct Costs', accountingHelpers.formatCurrency(summary.total_expenses - (summary.operating_expenses || 0))],
                                            ['Gross Profit', <Text fontWeight="semibold" tone={accountingHelpers.getProfitLossColor(summary.gross_profit)}>{accountingHelpers.formatCurrency(summary.gross_profit)}</Text>],
                                            ['Gross Margin %', <Text fontWeight="semibold">{accountingHelpers.formatPercentage(summary.gross_margin_percent)}</Text>],
                                            ['Operating Expenses', accountingHelpers.formatCurrency(summary.operating_expenses || 0)],
                                            ['Net Profit', <Text fontWeight="bold" tone={accountingHelpers.getProfitLossColor(summary.net_profit)}>{accountingHelpers.formatCurrency(summary.net_profit)}</Text>],
                                            ['Net Margin %', <Text fontWeight="bold">{accountingHelpers.formatPercentage(summary.net_margin_percent)}</Text>]
                                        ]}
                                    />
                                </div>
                            </BlockStack>
                        </Card>
                    </Layout.Section>
                </Layout>
            </BlockStack>
        );
    };

    const renderProductProfitabilityReport = () => {
        if (!reportData || !reportData.products) return null;

        const rows = reportData.products.map(product => [
            <Text fontWeight="semibold">{product.product_name || 'Unknown Product'}</Text>,
            product.units_sold,
            accountingHelpers.formatCurrency(product.total_revenue),
            accountingHelpers.formatCurrency(product.total_cost),
            <Text tone={accountingHelpers.getProfitLossColor(product.profit)}>
                {accountingHelpers.formatCurrency(product.profit)}
            </Text>,
            <Text tone={accountingHelpers.getMarginColor(product.margin_percent)}>
                {accountingHelpers.formatPercentage(product.margin_percent)}
            </Text>
        ]);

        return (
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                        Product Profitability Analysis
                    </Text>
                    <DataTable
                        columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                        headings={['Product', 'Units Sold', 'Revenue', 'Cost', 'Profit', 'Margin %']}
                        rows={rows}
                        totals={[
                            'Total',
                            reportData.products.reduce((sum, p) => sum + p.units_sold, 0),
                            accountingHelpers.formatCurrency(reportData.products.reduce((sum, p) => sum + p.total_revenue, 0)),
                            accountingHelpers.formatCurrency(reportData.products.reduce((sum, p) => sum + p.total_cost, 0)),
                            accountingHelpers.formatCurrency(reportData.products.reduce((sum, p) => sum + p.profit, 0)),
                            ''
                        ]}
                        showTotalsInFooter
                    />
                </BlockStack>
            </Card>
        );
    };

    const renderCustomerProfitabilityReport = () => {
        if (!reportData || !reportData.customers) return null;

        const rows = reportData.customers.map(customer => [
            <Text fontWeight="semibold">{customer.customer_name || 'Guest Customer'}</Text>,
            customer.customer_email || '-',
            customer.order_count,
            accountingHelpers.formatCurrency(customer.total_revenue),
            accountingHelpers.formatCurrency(customer.average_order_value),
            <Text tone={accountingHelpers.getProfitLossColor(customer.profit)}>
                {accountingHelpers.formatCurrency(customer.profit)}
            </Text>
        ]);

        return (
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                        Customer Profitability Analysis
                    </Text>
                    <DataTable
                        columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric']}
                        headings={['Customer', 'Email', 'Orders', 'Revenue', 'Avg Order Value', 'Profit']}
                        rows={rows}
                        totals={[
                            'Total',
                            '',
                            reportData.customers.reduce((sum, c) => sum + c.order_count, 0),
                            accountingHelpers.formatCurrency(reportData.customers.reduce((sum, c) => sum + c.total_revenue, 0)),
                            '',
                            accountingHelpers.formatCurrency(reportData.customers.reduce((sum, c) => sum + c.profit, 0))
                        ]}
                        showTotalsInFooter
                    />
                </BlockStack>
            </Card>
        );
    };

    const renderRevenueBreakdownReport = () => {
        if (!reportData) return null;

        const { by_product, by_payment_method } = reportData;

        return (
            <BlockStack gap="400">
                {by_product && by_product.length > 0 && (
                    <Card>
                        <BlockStack gap="400">
                            <Text variant="headingMd" as="h2">
                                Revenue by Product
                            </Text>
                            <DataTable
                                columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                                headings={['Product', 'Revenue', 'Orders', 'Percentage']}
                                rows={by_product.map(item => [
                                    item.product_name || 'Unknown',
                                    accountingHelpers.formatCurrency(item.revenue),
                                    item.order_count,
                                    <Badge>{item.percentage.toFixed(1)}%</Badge>
                                ])}
                            />
                        </BlockStack>
                    </Card>
                )}

                {by_payment_method && by_payment_method.length > 0 && (
                    <Card>
                        <BlockStack gap="400">
                            <Text variant="headingMd" as="h2">
                                Revenue by Payment Method
                            </Text>
                            <DataTable
                                columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                                headings={['Payment Method', 'Revenue', 'Orders', 'Percentage']}
                                rows={by_payment_method.map(item => [
                                    item.payment_method || 'Unknown',
                                    accountingHelpers.formatCurrency(item.revenue),
                                    item.order_count,
                                    <Badge>{item.percentage.toFixed(1)}%</Badge>
                                ])}
                            />
                        </BlockStack>
                    </Card>
                )}
            </BlockStack>
        );
    };

    const renderExpenseBreakdownReport = () => {
        if (!reportData || !reportData.summary) return null;

        return <ExpenseSummary summary={reportData} />;
    };

    const renderPaymentMethodAnalysisReport = () => {
        if (!reportData || !reportData.payment_methods) return null;

        const rows = reportData.payment_methods.map(method => [
            <Text fontWeight="semibold">{method.payment_method || 'Unknown'}</Text>,
            method.order_count,
            accountingHelpers.formatCurrency(method.total_revenue),
            accountingHelpers.formatCurrency(method.average_order_value),
            accountingHelpers.formatCurrency(method.total_fees),
            <Badge>{method.percentage.toFixed(1)}%</Badge>
        ]);

        return (
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                        Payment Method Analysis
                    </Text>
                    <DataTable
                        columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'text']}
                        headings={['Payment Method', 'Orders', 'Revenue', 'Avg Order', 'Fees', 'Share']}
                        rows={rows}
                        totals={[
                            'Total',
                            reportData.payment_methods.reduce((sum, m) => sum + m.order_count, 0),
                            accountingHelpers.formatCurrency(reportData.payment_methods.reduce((sum, m) => sum + m.total_revenue, 0)),
                            '',
                            accountingHelpers.formatCurrency(reportData.payment_methods.reduce((sum, m) => sum + m.total_fees, 0)),
                            '100%'
                        ]}
                        showTotalsInFooter
                    />
                </BlockStack>
            </Card>
        );
    };

    const renderReportContent = () => {
        if (loading) {
            return (
                <Card>
                    <div style={{ padding: '3rem', textAlign: 'center' }}>
                        <Spinner size="large" />
                        <div style={{ marginTop: '1rem' }}>
                            <Text>Generating report...</Text>
                        </div>
                    </div>
                </Card>
            );
        }

        if (!reportData) {
            return (
                <Card>
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <Text tone="subdued">Select report parameters and click "Generate Report"</Text>
                    </div>
                </Card>
            );
        }

        switch (reportType) {
            case 'profit_loss':
                return renderProfitLossReport();
            case 'product_profitability':
                return renderProductProfitabilityReport();
            case 'customer_profitability':
                return renderCustomerProfitabilityReport();
            case 'revenue_breakdown':
                return renderRevenueBreakdownReport();
            case 'expense_breakdown':
                return renderExpenseBreakdownReport();
            case 'payment_method_analysis':
                return renderPaymentMethodAnalysisReport();
            default:
                return null;
        }
    };

    return (
        <Page
            title="Financial Reports"
            subtitle="Generate and view detailed financial reports"
        >
            <Layout>
                {error && (
                    <Layout.Section>
                        <Banner tone="critical" title="Error" onDismiss={() => setError(null)}>
                            <p>{error}</p>
                        </Banner>
                    </Layout.Section>
                )}

                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text variant="headingMd" as="h2">
                                Report Parameters
                            </Text>

                            <FormLayout>
                                <FormLayout.Group>
                                    <Select
                                        label="Report Type"
                                        options={reportTypeOptions}
                                        value={reportType}
                                        onChange={setReportType}
                                    />

                                    <Select
                                        label="Period"
                                        options={[
                                            ...getAccountingPeriodOptions(),
                                            { label: 'Custom Range', value: 'custom' }
                                        ]}
                                        value={periodType}
                                        onChange={setPeriodType}
                                    />

                                    {reportType === 'profit_loss' && (
                                        <Select
                                            label="Group By"
                                            options={groupByOptions}
                                            value={groupBy}
                                            onChange={setGroupBy}
                                        />
                                    )}
                                </FormLayout.Group>

                                {periodType === 'custom' && (
                                    <FormLayout.Group>
                                        <TextField
                                            label="Start Date"
                                            type="date"
                                            value={startDate}
                                            onChange={setStartDate}
                                            autoComplete="off"
                                        />
                                        <TextField
                                            label="End Date"
                                            type="date"
                                            value={endDate}
                                            onChange={setEndDate}
                                            autoComplete="off"
                                        />
                                    </FormLayout.Group>
                                )}
                            </FormLayout>

                            <InlineStack align="end">
                                <Button
                                    primary
                                    onClick={handleGenerateReport}
                                    loading={loading}
                                >
                                    Generate Report
                                </Button>
                            </InlineStack>
                        </BlockStack>
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    {renderReportContent()}
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default ReportsPage;
